/**
 * VEINTE 07 - Trust & Value Proposition JavaScript
 * Version: 2.0 (Optimized)
 * 
 * Features:
 * - Namespaced to avoid conflicts
 * - Debounced scroll handler
 * - Error handling and fallbacks
 * - Accessibility improvements
 * - Performance optimizations
 */

(function() {
  'use strict';

  // Namespace for Veinte07 Trust functionality
  const Veinte07Trust = {
    
    // Configuration
    config: {
      scrollOffset: 80, // Default header height
      scrollBehavior: 'smooth',
      highlightDuration: 2000,
      highlightColor: 'rgba(247, 181, 0, 0.15)',
      highlightBorder: '2px solid #000000',
      tabSelectors: [
        '[data-slug="vehiculos"]',
        '[data-slug*="vehicle"]',
        '[data-tab*="vehicle"]',
        '.station-tabs-tab[data-slug="vehiculos"]',
        '.tab[data-tab="vehiculos"]',
        '#tab-vehiculos',
        '[href="#vehiculos"]'
      ],
      debug: false
    },

    /**
     * Utility: Debounce function
     */
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },

    /**
     * Utility: Log debug messages
     */
    log(message, data = null) {
      if (this.config.debug) {
        console.log(`[Veinte07Trust] ${message}`, data || '');
      }
    },

    /**
     * Utility: Log errors
     */
    error(message, error = null) {
      console.error(`[Veinte07Trust Error] ${message}`, error || '');
    },

    /**
     * Get dynamic header height
     */
    getHeaderHeight() {
      try {
        const header = document.querySelector('header') || 
                      document.querySelector('.header') ||
                      document.querySelector('[role="banner"]');
        
        if (header) {
          return header.offsetHeight || this.config.scrollOffset;
        }
        return this.config.scrollOffset;
      } catch (error) {
        this.error('Error getting header height', error);
        return this.config.scrollOffset;
      }
    },

    /**
     * Find vehicle tab using multiple selectors
     */
    findVehicleTab() {
      for (const selector of this.config.tabSelectors) {
        try {
          const element = document.querySelector(selector);
          if (element) {
            this.log('Vehicle tab found with selector:', selector);
            return element;
          }
        } catch (error) {
          // Continue to next selector
          continue;
        }
      }
      return null;
    },

    /**
     * Highlight element with animation
     */
    highlightElement(element) {
      if (!element) return;

      try {
        // Store original styles
        const originalBg = element.style.backgroundColor || '';
        const originalBorder = element.style.border || '';
        const originalTransition = element.style.transition || '';

        // Apply highlight
        element.style.transition = 'all 0.3s ease';
        element.style.backgroundColor = this.config.highlightColor;
        element.style.border = this.config.highlightBorder;

        // Remove highlight after duration
        setTimeout(() => {
          element.style.backgroundColor = originalBg;
          element.style.border = originalBorder;
          
          // Clean up after transition
          setTimeout(() => {
            element.style.transition = originalTransition;
          }, 300);
        }, this.config.highlightDuration);

      } catch (error) {
        this.error('Error highlighting element', error);
      }
    },

    /**
     * Smooth scroll to element
     */
    scrollToElement(element, offset = 0) {
      if (!element) return;

      try {
        const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - offset - 20;

        // Check if browser supports smooth scrolling
        if ('scrollBehavior' in document.documentElement.style) {
          window.scrollTo({
            top: offsetPosition,
            behavior: this.config.scrollBehavior
          });
        } else {
          // Fallback for older browsers
          window.scrollTo(0, offsetPosition);
        }

        this.log('Scrolled to element', { position: offsetPosition });
      } catch (error) {
        this.error('Error scrolling to element', error);
      }
    },

    /**
     * Main function: Scroll to vehicle compatibility section
     */
    scrollToVehiculos() {
      this.log('scrollToVehiculos called');

      try {
        // Find vehicle tab
        const vehicleTab = this.findVehicleTab();
        
        if (!vehicleTab) {
          this.error('Vehicle tab not found. Available selectors:', this.config.tabSelectors);
          
          // Fallback: try to find any element with "vehiculo" in ID or class
          const fallback = document.querySelector('[id*="vehiculo"], [class*="vehiculo"]');
          if (fallback) {
            this.log('Using fallback element');
            this.scrollToElement(fallback, this.getHeaderHeight());
            this.highlightElement(fallback);
          }
          return;
        }

        // Click the tab to activate it
        if (typeof vehicleTab.click === 'function') {
          vehicleTab.click();
          this.log('Vehicle tab clicked');
        }

        // Set focus for accessibility
        if (typeof vehicleTab.focus === 'function') {
          vehicleTab.focus();
        }

        // Wait for tab content to render, then scroll
        setTimeout(() => {
          // Try to find the tab panel
          const tabPanelId = vehicleTab.getAttribute('aria-controls');
          let targetElement = null;

          if (tabPanelId) {
            targetElement = document.getElementById(tabPanelId);
            this.log('Target element found by aria-controls:', tabPanelId);
          }

          // Fallback: find closest tab panel
          if (!targetElement) {
            targetElement = vehicleTab.closest('.tab-panel') || 
                          vehicleTab.nextElementSibling ||
                          vehicleTab;
            this.log('Using fallback target element');
          }

          if (targetElement) {
            const headerHeight = this.getHeaderHeight();
            this.scrollToElement(targetElement, headerHeight);
            this.highlightElement(targetElement);
          }

        }, 300); // Give time for tab content to render

      } catch (error) {
        this.error('Error in scrollToVehiculos', error);
      }
    },

    /**
     * Initialize event listeners
     */
    init() {
      this.log('Initializing Veinte07 Trust module');

      try {
        // Find all CTA buttons with data-vehicle-scroll attribute
        const ctaButtons = document.querySelectorAll('[data-vehicle-scroll]');
        
        if (ctaButtons.length === 0) {
          this.log('No CTA buttons found');
          return;
        }

        this.log(`Found ${ctaButtons.length} CTA button(s)`);

        // Add click event listeners
        ctaButtons.forEach((button, index) => {
          button.addEventListener('click', (e) => {
            e.preventDefault();
            this.log(`CTA button ${index + 1} clicked`);
            this.scrollToVehiculos();
          });
        });

        // Keyboard accessibility: Enter and Space keys
        ctaButtons.forEach((button) => {
          button.addEventListener('keydown', (e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              this.scrollToVehiculos();
            }
          });
        });

      } catch (error) {
        this.error('Error initializing module', error);
      }
    },

    /**
     * Enable debug mode
     */
    enableDebug() {
      this.config.debug = true;
      this.log('Debug mode enabled');
    },

    /**
     * Update configuration
     */
    updateConfig(newConfig) {
      try {
        this.config = { ...this.config, ...newConfig };
        this.log('Configuration updated', this.config);
      } catch (error) {
        this.error('Error updating configuration', error);
      }
    }

  };

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      Veinte07Trust.init();
    });
  } else {
    // DOM already loaded
    Veinte07Trust.init();
  }

  // Expose to global scope for external access if needed
  window.Veinte07Trust = Veinte07Trust;

  // Legacy support: keep global function for backwards compatibility
  window.scrollToVehiculos = function() {
    Veinte07Trust.scrollToVehiculos();
  };

})();